import { useState, useEffect } from "react";
import { 
  Calendar, Clock, MapPin, Car, Repeat, Plus, Trash2, 
  Play, Pause, ChevronDown, Users, Accessibility, Check
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { apiClient } from "@/App";

const DAYS_OF_WEEK = [
  { value: 0, label: "Mon" },
  { value: 1, label: "Tue" },
  { value: 2, label: "Wed" },
  { value: 3, label: "Thu" },
  { value: 4, label: "Fri" },
  { value: 5, label: "Sat" },
  { value: 6, label: "Sun" },
];

export default function RecurringRidesManager({ clients = [], drivers = [], onRidesGenerated }) {
  const [recurringRides, setRecurringRides] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    client_id: "",
    client_name: "",
    client_phone: "",
    pickup_address: "",
    dropoff_address: "",
    pickup_time: "09:00",
    vehicle_type: "car",
    recurrence_type: "weekly",
    days_of_week: [],
    start_date: new Date().toISOString().split('T')[0],
    end_date: "",
    special_requests: "",
    preferred_driver: "none",
    ride_sharing_preference: "alone"
  });

  useEffect(() => {
    fetchRecurringRides();
  }, []);

  const fetchRecurringRides = async () => {
    try {
      const response = await apiClient.get("/recurring-rides");
      setRecurringRides(response.data);
    } catch (error) {
      console.error("Failed to fetch recurring rides:", error);
    }
  };

  const handleClientSelect = (clientId) => {
    const client = clients.find(c => c.id === clientId);
    if (client) {
      setFormData({
        ...formData,
        client_id: clientId,
        client_name: client.name,
        client_phone: client.phone || "",
        pickup_address: client.address || ""
      });
    }
  };

  const toggleDay = (dayValue) => {
    const currentDays = formData.days_of_week;
    if (currentDays.includes(dayValue)) {
      setFormData({
        ...formData,
        days_of_week: currentDays.filter(d => d !== dayValue)
      });
    } else {
      setFormData({
        ...formData,
        days_of_week: [...currentDays, dayValue].sort()
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (formData.days_of_week.length === 0) {
      toast.error("Please select at least one day");
      return;
    }

    setIsLoading(true);
    try {
      const payload = {
        client_id: formData.client_id,
        client_name: formData.client_name,
        client_phone: formData.client_phone,
        pickup_locations: [{ address: formData.pickup_address, order: 0 }],
        dropoff_address: formData.dropoff_address,
        pickup_time: formData.pickup_time,
        vehicle_type: formData.vehicle_type,
        recurrence_type: formData.recurrence_type,
        days_of_week: formData.days_of_week,
        start_date: formData.start_date,
        end_date: formData.end_date || null,
        special_requests: formData.special_requests,
        preferred_driver: formData.preferred_driver === "none" ? null : formData.preferred_driver,
        ride_sharing_preference: formData.ride_sharing_preference
      };

      await apiClient.post("/recurring-rides", payload);
      toast.success("Recurring ride created!");
      setShowForm(false);
      fetchRecurringRides();
      
      // Reset form
      setFormData({
        client_id: "",
        client_name: "",
        client_phone: "",
        pickup_address: "",
        dropoff_address: "",
        pickup_time: "09:00",
        vehicle_type: "car",
        recurrence_type: "weekly",
        days_of_week: [],
        start_date: new Date().toISOString().split('T')[0],
        end_date: "",
        special_requests: "",
        preferred_driver: "none",
        ride_sharing_preference: "alone"
      });
    } catch (error) {
      toast.error("Failed to create recurring ride");
    } finally {
      setIsLoading(false);
    }
  };

  const generateRides = async (recurringId, weeks = 4) => {
    try {
      const response = await apiClient.post(`/recurring-rides/${recurringId}/generate?weeks=${weeks}`);
      toast.success(`Generated ${response.data.rides_created} rides!`);
      if (onRidesGenerated) onRidesGenerated();
    } catch (error) {
      toast.error("Failed to generate rides");
    }
  };

  const deleteRecurring = async (recurringId) => {
    if (!confirm("Delete this recurring schedule?")) return;
    
    try {
      await apiClient.delete(`/recurring-rides/${recurringId}`);
      toast.success("Recurring schedule deleted");
      fetchRecurringRides();
    } catch (error) {
      toast.error("Failed to delete");
    }
  };

  return (
    <div className="space-y-4" data-testid="recurring-rides-manager">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Repeat className="w-5 h-5 text-violet-500" />
          <h3 className="font-bold text-lg">Recurring Rides</h3>
          <Badge variant="secondary">{recurringRides.length} schedules</Badge>
        </div>
        <Button 
          onClick={() => setShowForm(!showForm)}
          size="sm"
          className="bg-violet-500 hover:bg-violet-600"
        >
          <Plus className="w-4 h-4 mr-1" />
          New Schedule
        </Button>
      </div>

      {/* Create Form */}
      {showForm && (
        <Card className="border-violet-200 bg-violet-50/50">
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Create Recurring Schedule</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Client Selection */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Client</Label>
                  <Select value={formData.client_id} onValueChange={handleClientSelect}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select client" />
                    </SelectTrigger>
                    <SelectContent>
                      {clients.map(client => (
                        <SelectItem key={client.id} value={client.id}>
                          {client.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Phone</Label>
                  <Input 
                    value={formData.client_phone}
                    onChange={(e) => setFormData({...formData, client_phone: e.target.value})}
                    placeholder="(555) 123-4567"
                  />
                </div>
              </div>

              {/* Locations */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Pickup Address</Label>
                  <Input 
                    value={formData.pickup_address}
                    onChange={(e) => setFormData({...formData, pickup_address: e.target.value})}
                    placeholder="123 Main St"
                    required
                  />
                </div>
                <div>
                  <Label>Dropoff Address</Label>
                  <Input 
                    value={formData.dropoff_address}
                    onChange={(e) => setFormData({...formData, dropoff_address: e.target.value})}
                    placeholder="456 Medical Center"
                    required
                  />
                </div>
              </div>

              {/* Time and Vehicle */}
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label>Pickup Time</Label>
                  <Input 
                    type="time"
                    value={formData.pickup_time}
                    onChange={(e) => setFormData({...formData, pickup_time: e.target.value})}
                    required
                  />
                </div>
                <div>
                  <Label>Vehicle Type</Label>
                  <Select value={formData.vehicle_type} onValueChange={(v) => setFormData({...formData, vehicle_type: v})}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="car">Car</SelectItem>
                      <SelectItem value="van">Van</SelectItem>
                      <SelectItem value="handicap_van">Wheelchair Van</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Preferred Driver</Label>
                  <Select value={formData.preferred_driver} onValueChange={(v) => setFormData({...formData, preferred_driver: v})}>
                    <SelectTrigger>
                      <SelectValue placeholder="No preference" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">No preference</SelectItem>
                      {drivers.map(driver => (
                        <SelectItem key={driver.id} value={driver.id}>
                          {driver.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Days of Week */}
              <div>
                <Label className="mb-2 block">Days of Week</Label>
                <div className="flex gap-2">
                  {DAYS_OF_WEEK.map(day => (
                    <button
                      key={day.value}
                      type="button"
                      onClick={() => toggleDay(day.value)}
                      className={`w-10 h-10 rounded-lg font-semibold text-sm transition-all ${
                        formData.days_of_week.includes(day.value)
                          ? 'bg-violet-500 text-white shadow-lg'
                          : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                      }`}
                    >
                      {day.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Date Range */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Start Date</Label>
                  <Input 
                    type="date"
                    value={formData.start_date}
                    onChange={(e) => setFormData({...formData, start_date: e.target.value})}
                    required
                  />
                </div>
                <div>
                  <Label>End Date (Optional)</Label>
                  <Input 
                    type="date"
                    value={formData.end_date}
                    onChange={(e) => setFormData({...formData, end_date: e.target.value})}
                    placeholder="Leave empty for indefinite"
                  />
                </div>
              </div>

              {/* Actions */}
              <div className="flex gap-2 pt-2">
                <Button type="submit" disabled={isLoading} className="bg-violet-500 hover:bg-violet-600">
                  {isLoading ? "Creating..." : "Create Schedule"}
                </Button>
                <Button type="button" variant="outline" onClick={() => setShowForm(false)}>
                  Cancel
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      {/* List of Recurring Rides */}
      <div className="space-y-3">
        {recurringRides.length === 0 ? (
          <div className="text-center py-8 text-slate-500">
            <Repeat className="w-10 h-10 mx-auto mb-2 opacity-30" />
            <p>No recurring schedules yet</p>
            <p className="text-sm">Create one to auto-generate rides</p>
          </div>
        ) : (
          recurringRides.map((ride) => (
            <Card key={ride.id} className="border-slate-200">
              <CardContent className="p-4">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="font-bold">{ride.client_name}</span>
                      <Badge className={ride.is_active ? "bg-green-500" : "bg-slate-400"}>
                        {ride.is_active ? "Active" : "Paused"}
                      </Badge>
                    </div>
                    
                    <div className="text-sm text-slate-600 space-y-1">
                      <div className="flex items-center gap-2">
                        <MapPin className="w-4 h-4 text-blue-500" />
                        <span>{ride.pickup_locations?.[0]?.address} → {ride.dropoff_address}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Clock className="w-4 h-4 text-amber-500" />
                        <span>{ride.pickup_time}</span>
                        <span className="text-slate-400">|</span>
                        <span className="font-medium">
                          {(ride.days_of_week || []).map(d => DAYS_OF_WEEK.find(day => day.value === d)?.label).join(", ")}
                        </span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <Button 
                      size="sm" 
                      variant="outline"
                      onClick={() => generateRides(ride.id, 4)}
                      className="text-violet-600 border-violet-300 hover:bg-violet-50"
                    >
                      <Play className="w-4 h-4 mr-1" />
                      Generate 4 Weeks
                    </Button>
                    <Button 
                      size="sm" 
                      variant="ghost"
                      onClick={() => deleteRecurring(ride.id)}
                      className="text-red-500 hover:text-red-700 hover:bg-red-50"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>
    </div>
  );
}
